import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowRight, CreditCard } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 10;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollAttempts >= maxPollAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        return;
      } else if (data.status === 'expired' || data.payment_status === 'expired') {
        setStatus('expired');
        return;
      }
      
      // Continue polling
      setPollAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPollAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold">
              <span className="text-slate-800">local</span>
              <span className="text-orange-500">Tradies</span>
              <span className="text-slate-800">Online</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            {status === 'loading' && (
              <>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                  Processing Payment
                </h1>
                <p className="text-slate-600 mb-6">
                  Please wait while we confirm your payment...
                </p>
                <div className="flex justify-center">
                  <div className="flex space-x-1">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                        style={{ animationDelay: `${i * 0.1}s` }}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                  Payment Successful!
                </h1>
                <p className="text-slate-600 mb-6">
                  {paymentData?.message || 'Your credits have been added to your account.'}
                </p>
                {paymentData?.credits_added && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="text-3xl font-bold text-green-600">
                      +{paymentData.credits_added}
                    </div>
                    <div className="text-sm text-green-700">credits added</div>
                  </div>
                )}
                <div className="space-y-3">
                  <Link to="/browse">
                    <Button className="w-full bg-orange-500 hover:bg-orange-600">
                      Start Finding Leads
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/buy-credits">
                    <Button variant="outline" className="w-full">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Buy More Credits
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === 'expired' && (
              <>
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-8 h-8 text-yellow-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                  Session Expired
                </h1>
                <p className="text-slate-600 mb-6">
                  Your payment session has expired. Please try again.
                </p>
                <Link to="/buy-credits">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600">
                    Try Again
                  </Button>
                </Link>
              </>
            )}

            {status === 'timeout' && (
              <>
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-8 h-8 text-orange-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                  Taking Longer Than Expected
                </h1>
                <p className="text-slate-600 mb-6">
                  We're still processing your payment. Please check your email for confirmation or contact support if you don't receive it within a few minutes.
                </p>
                <div className="space-y-3">
                  <Button 
                    onClick={() => {
                      setPollAttempts(0);
                      setStatus('loading');
                      pollPaymentStatus();
                    }}
                    className="w-full"
                  >
                    Check Again
                  </Button>
                  <Link to="/">
                    <Button variant="outline" className="w-full">
                      Return Home
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                  Something Went Wrong
                </h1>
                <p className="text-slate-600 mb-6">
                  We couldn't verify your payment. Please contact support if you were charged.
                </p>
                <Link to="/buy-credits">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600">
                    Return to Credits
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
